// Piezas visuales de Obra › Clima (blanco, como el resto de LisualPro; íconos 3D y cielo del Dashboard IA).
import React, { useId } from 'react';
import { WX_ICONS, SkyDefs, SkyBand, SKY_CSS } from '../../IADashboard/clima/Sky';
import R from './reglasClima';

export const ESTADOS = {
  apto: { txt: 'Apto', emoji: '✅', chip: 'bg-emerald-50 text-emerald-700 border-emerald-200', fondo: 'bg-emerald-50', borde: 'border-emerald-300', punto: 'bg-emerald-500', hex: '#10b981', suave: '#d1fae5' },
  cuidado: { txt: 'Con cuidado', emoji: '⚠️', chip: 'bg-amber-50 text-amber-800 border-amber-200', fondo: 'bg-amber-50', borde: 'border-amber-300', punto: 'bg-amber-500', hex: '#f59e0b', suave: '#fef3c7' },
  no: { txt: 'No apto', emoji: '⛔', chip: 'bg-rose-50 text-rose-700 border-rose-200', fondo: 'bg-rose-50', borde: 'border-rose-300', punto: 'bg-rose-500', hex: '#e11d48', suave: '#ffe4e6' },
};

export function Semaforo({ estado, small, className = '', children }) {
  const e = ESTADOS[estado];
  if (!e) return null;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border font-semibold whitespace-nowrap ${small ? 'text-[10px] px-1.5 py-0.5' : 'text-xs px-2 py-0.5'} ${e.chip} ${className}`}>
      <span aria-hidden="true">{e.emoji}</span>{children || e.txt}
    </span>
  );
}

export function Icono3D({ kind, size = 40, className = '', title }) {
  const src = WX_ICONS[kind] || WX_ICONS.nublado;
  return <img src={src} alt={title || R.TIEMPO_TXT[kind] || ''} title={title} width={size} height={size} className={`select-none ${className}`} style={{ width: size, height: size, filter: 'drop-shadow(0 2px 3px rgba(15,23,42,.18))' }} draggable={false} />;
}

export function Seccion({ titulo, sub, right, children, className = '', bodyClass = '' }) {
  return (
    <section className={`bg-white border border-gray-200 rounded-2xl shadow-sm min-w-0 ${className}`}>
      {(titulo || right) && (
        <header className="flex items-start justify-between gap-3 flex-wrap px-4 pt-3.5 pb-2.5 border-b border-gray-100">
          <div className="min-w-0">
            <h3 className="text-[15px] font-semibold text-gray-900 leading-tight">{titulo}</h3>
            {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
          </div>
          {right && <div className="flex items-center gap-2 flex-wrap">{right}</div>}
        </header>
      )}
      <div className={`p-4 ${bodyClass}`}>{children}</div>
    </section>
  );
}

export function Kpi({ label, valor, sub, tono = 'gris', icon, grande }) {
  const T = {
    gris: 'bg-gray-50 border-gray-200', verde: 'bg-emerald-50 border-emerald-200', ambar: 'bg-amber-50 border-amber-200',
    rojo: 'bg-rose-50 border-rose-200', azul: 'bg-sky-50 border-sky-200', violeta: 'bg-indigo-50 border-indigo-200',
  }[tono];
  return (
    <div className={`rounded-xl border px-3 py-2.5 min-w-0 ${T}`}>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 flex items-center gap-1.5 leading-tight">{icon}{label}</p>
      <p className={`${grande ? 'text-2xl' : 'text-xl'} font-bold text-gray-900 leading-tight mt-0.5 tabular-nums`}>{valor}</p>
      {sub && <p className="text-[11px] text-gray-600 leading-snug mt-0.5">{sub}</p>}
    </div>
  );
}

/** Cielo animado de fondo (mismo dibujo que las columnas del Dashboard IA, a pantalla completa). */
export function Cielo({ kind, level = 2, night = false, animate = true, className = '', alto = 360 }) {
  const uid = 'cl' + useId().replace(/[^a-zA-Z0-9]/g, '');
  return (
    <div className={`absolute inset-0 overflow-hidden ${className}`} aria-hidden="true">
      <style>{SKY_CSS}</style>
      {/* sin viewBox: las gotas y la nieve mantienen su tamaño real en cualquier ancho */}
      <svg width="100%" height="100%" className="absolute inset-0">
        <defs><SkyDefs uid={uid} /></defs>
        <SkyBand x={0} y={0} width={2400} height={alto} uid={uid} band={{ a: 0, kind, level, night }} animate={animate} />
      </svg>
    </div>
  );
}

/** Anillo de puntaje 0-100. */
export function Anillo({ valor, size = 56, grosor = 6, color }) {
  const r = (size - grosor) / 2, c = 2 * Math.PI * r;
  const col = color || (valor >= 80 ? '#10b981' : valor >= 60 ? '#f59e0b' : '#e11d48');
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="shrink-0" role="img" aria-label={`${valor} de 100`}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#eef2f7" strokeWidth={grosor} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={col} strokeWidth={grosor} strokeLinecap="round" strokeDasharray={`${(c * valor) / 100} ${c}`} transform={`rotate(-90 ${size / 2} ${size / 2})`} />
      <text x="50%" y="52%" dominantBaseline="middle" textAnchor="middle" fontSize={size * 0.3} fontWeight="700" fill="#111827">{valor}</text>
    </svg>
  );
}

/** Flecha de viento: apunta hacia donde VA el viento (la dirección meteorológica es de dónde viene). */
export function Brujula({ deg, size = 54 }) {
  const ok = deg != null;
  return (
    <svg width={size} height={size} viewBox="0 0 54 54" className="shrink-0" aria-hidden="true">
      <circle cx="27" cy="27" r="25" fill="#f8fafc" stroke="#e2e8f0" />
      {['N', 'E', 'S', 'O'].map((t, i) => (
        <text key={t} x={27 + 19 * Math.sin((i * Math.PI) / 2)} y={27 - 19 * Math.cos((i * Math.PI) / 2) + 3} textAnchor="middle" fontSize="8" fontWeight="700" fill={t === 'N' ? '#e11d48' : '#94a3b8'}>{t}</text>
      ))}
      {ok && (
        <g transform={`rotate(${(deg + 180) % 360} 27 27)`}>
          <path d="M27 11 L32 29 L27 25 L22 29 Z" fill="#2563eb" />
          <rect x="25.6" y="25" width="2.8" height="15" rx="1.4" fill="#93c5fd" />
        </g>
      )}
    </svg>
  );
}

/** Barra de pestañas con íconos (scroll horizontal en el celular). */
export function Pestanas({ tabs, activa, onCambiar }) {
  return (
    <div className="-mx-1 overflow-x-auto clima-hide-scroll" role="tablist" aria-label="Secciones del clima">
      <div className="flex gap-1.5 px-1 min-w-max">
        {tabs.map((t) => (
          <button key={t.key} type="button" role="tab" aria-selected={activa === t.key} onClick={() => onCambiar(t.key)}
            className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-semibold transition-colors whitespace-nowrap min-h-[40px] ${activa === t.key ? 'bg-gray-900 text-white shadow-sm' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50 hover:text-gray-900'}`}>
            <span aria-hidden="true">{t.emoji}</span>{t.label}
            {t.badge != null && <span className={`ml-0.5 text-[10px] rounded-full px-1.5 py-0.5 ${activa === t.key ? 'bg-white/20' : 'bg-rose-100 text-rose-700'}`}>{t.badge}</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

export function Cargando({ txt = 'Cargando…', className = '' }) {
  return (
    <div className={`flex items-center justify-center gap-2 text-sm text-gray-500 py-10 ${className}`}>
      <span className="w-4 h-4 rounded-full border-2 border-gray-300 border-t-blue-600 animate-spin" />{txt}
    </div>
  );
}

export const CLIMA_CSS = `
.clima-hide-scroll { scrollbar-width: none; -ms-overflow-style: none; }
.clima-hide-scroll::-webkit-scrollbar { display: none; }
@keyframes climaIn { from { opacity: 0; transform: translateY(6px) } to { opacity: 1; transform: none } }
.clima-in { animation: climaIn .35s ease-out backwards } /* sin "forwards": un transform que queda activo rompe los modales position:fixed de adentro */
@keyframes climaFloat { 0%,100% { transform: translateY(0) } 50% { transform: translateY(-5px) } }
.clima-float { animation: climaFloat 5s ease-in-out infinite }
.clima-rayas { background-image: repeating-linear-gradient(135deg, rgba(255,255,255,.0) 0 6px, rgba(255,255,255,.65) 6px 9px); }
@media (prefers-reduced-motion: reduce) { .clima-in, .clima-float { animation: none } }
`;

// Formatos cortos
export const DOW = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];
export const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
export const diaCorto = (f) => `${DOW[new Date(f + 'T12:00:00Z').getUTCDay()]} ${Number(f.slice(8, 10))}/${Number(f.slice(5, 7))}`;
export const mm = (v) => (v == null ? '—' : v < 0.1 ? '0' : v < 10 ? v.toFixed(1).replace('.', ',') : String(Math.round(v)));
export const grados = (v) => (v == null ? '—' : `${Math.round(v)}°`);
export const nivelLluvia = (mmv, porHora) => (porHora ? (mmv < 0.5 ? 1 : mmv < 2.5 ? 2 : 3) : (mmv < 5 ? 1 : mmv < 15 ? 2 : 3));
